import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";
import { PACKAGES } from "../utils/entitlements";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import Navbar from "../components/Navbar";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  FaCreditCard,
  FaLock,
  FaSave,
  FaCheck,
  FaMoneyBillWave,
  FaTasks,
  FaHistory,
  FaQuoteRight,
  FaQuoteLeft,
  FaCog,
  FaSync,
  FaBell,
  FaChartLine,
  FaUserCog,
  FaShieldAlt,
  FaDatabase,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

// --- QUOTE CONFIGURATION ---
const QUOTE_RANGES = [
  { key: 0, label: "0% (Start)", dbKey: "0", shortLabel: "0%" },
  { key: 1, label: "1% to 39%", dbKey: "1", shortLabel: "1-39%" },
  { key: 40, label: "40% to 69%", dbKey: "40", shortLabel: "40-69%" },
  { key: 70, label: "70% to 89%", dbKey: "70", shortLabel: "70-89%" },
  { key: 90, label: "90% to 99%", dbKey: "90", shortLabel: "90-99%" },
  { key: 100, label: "100%", dbKey: "100", shortLabel: "100%" },
];

const HABIT_INITIAL_QUOTES = {
  0: "The day awaits! Time to start the first habit! 💥",
  1: "Let's conquer the day! Hard things first💪",
  40: "Good start — keep the momentum going!",
  70: "Almost there, stay consistent!",
  90: "Almost done — finish strong!",
  100: "Mission Accomplished! Good Job Soldier! Enjoy your day💪",
};

const TASK_INITIAL_QUOTES = {
  0: "Focus on your tasks! Don't let the day run you. 🏃",
  1: "Break it down! Tackle that first task now. 🎯",
  40: "You're on a roll! Keep checking things off. ✅",
  70: "The finish line is near. Momentum is everything! 💨",
  90: "Almost done — clear that list!",
  100: "Task list zeroed out! Enjoy the mental space. ✨",
};

// --- CURRENCY OPTIONS ---
const CURRENCY_OPTIONS = [
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
];

const DEFAULT_CURRENCY = { code: "EUR", symbol: "€", name: "Euro" };

// ----------------------------------------------------
//       REUSABLE COMPONENT: ProgressQuoteSettings
// ----------------------------------------------------
function ProgressQuoteSettings({ type, initialQuotes }) {
  const { t } = useTranslation();
  const { userId, subscriptionTier } = useAuth();
  const isFullOption = subscriptionTier === PACKAGES.PRO;
  const isHabitTracker = type === "habits";

  const settingsCollection = isHabitTracker ? "habitTracker" : "taskTracker";
  const settingsDocRef = userId
    ? doc(db, "users", userId, "settings", settingsCollection)
    : null;

  const [localQuotes, setLocalQuotes] = useState(initialQuotes);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [isLockedPreview, setIsLockedPreview] = useState(false);
  const [activeTab, setActiveTab] = useState("0");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!settingsDocRef || !userId) {
      setLoading(false);
      return;
    }

    const fetchQuotes = async () => {
      try {
        const docSnap = await getDoc(settingsDocRef);
        if (docSnap.exists() && docSnap.data().progressQuotes) {
          const fetched = docSnap.data().progressQuotes;
          const safeFetched = Object.fromEntries(
            Object.entries(fetched).map(([key, value]) => [String(key), value])
          );
          setLocalQuotes({ ...initialQuotes, ...safeFetched });
        } else {
          setLocalQuotes(initialQuotes);
        }
      } catch (error) {
        console.error(`Error fetching custom quotes for ${type}:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, [userId, type]);

  const handleChange = (dbKey, value) => {
    setLocalQuotes((prev) => ({
      ...prev,
      [String(dbKey)]: value,
    }));
  };

  const handleInteraction = (e) => {
    if (!isFullOption) {
      e.preventDefault();
      setIsLockedPreview(true);
    }
  };

  const handleSaveQuotes = async () => {
    if (!userId || !isFullOption) return;

    const allQuotesValid = Object.values(localQuotes).every(
      (q) => q && q.trim().length > 0
    );
    if (!allQuotesValid) {
      setStatus(t("settings.quotes.allQuotesRequired"));
      setTimeout(() => setStatus(""), 5000);
      return;
    }

    setStatus(t("settings.status.saving"));
    try {
      await setDoc(
        settingsDocRef,
        { progressQuotes: localQuotes },
        { merge: true }
      );
      setStatus(t("settings.quotes.savedSuccess"));
      setTimeout(() => setStatus(""), 3000);
    } catch (error) {
      console.error(`Error saving custom quotes for ${type}:`, error);
      setStatus(t("settings.status.error"));
      setTimeout(() => setStatus(""), 5000);
    }
  };

  const hasContent = Object.values(localQuotes).every(
    (q) => q && q.trim().length > 0
  );

  if (loading) {
    return (
      <QuoteSection>
        <QuoteHeader>
          <QuoteIcon>{isHabitTracker ? <FaHistory /> : <FaTasks />}</QuoteIcon>
          <QuoteTitle>
            {isHabitTracker
              ? t("settings.quotes.habits")
              : t("settings.quotes.tasks")}
          </QuoteTitle>
        </QuoteHeader>
        <LoadingState>{t("settings.quotes.loading")}</LoadingState>
      </QuoteSection>
    );
  }

  return (
    <QuoteSection>
      {!isFullOption && isLockedPreview && (
        <MobileUpgradeOverlay>
          <FaLock size={24} />
          <h3>{t("settings.upgrade.premiumFeature")}</h3>
          <p>{t("settings.upgrade.upgradeToCustomize")}</p>
          <MobileUpgradeLink href="/upgrade">
            {t("settings.upgrade.upgradeNow")}
          </MobileUpgradeLink>
        </MobileUpgradeOverlay>
      )}

      <QuoteHeader>
        <QuoteIcon>{isHabitTracker ? <FaHistory /> : <FaTasks />}</QuoteIcon>
        <QuoteTitle>
          {isHabitTracker
            ? t("settings.quotes.habits")
            : t("settings.quotes.tasks")}
        </QuoteTitle>
        <QuoteBadge $isPro={isFullOption}>
          {isFullOption
            ? t("settings.subscription.pro")
            : t("settings.subscription.free")}
        </QuoteBadge>
      </QuoteHeader>

      <QuoteDescription>{t("settings.quotes.description")}</QuoteDescription>

      <QuoteTabs $isMobile={isMobile}>
        {QUOTE_RANGES.map((range) => (
          <QuoteTab
            key={range.dbKey}
            $active={activeTab === range.dbKey}
            onClick={() => setActiveTab(range.dbKey)}
            $isMobile={isMobile}
          >
            {isMobile ? range.shortLabel : range.label}
          </QuoteTab>
        ))}
      </QuoteTabs>

      <QuoteContent>
        {QUOTE_RANGES.map((range) => (
          <QuoteInputGroup
            key={range.dbKey}
            $active={activeTab === range.dbKey}
          >
            <QuoteRangeLabel>
              <span>{range.label}</span>
              <small>
                {t("settings.quotes.activeFrom", { percent: range.key })}
              </small>
            </QuoteRangeLabel>
            <QuoteTextArea
              value={localQuotes[range.dbKey] || ""}
              onChange={(e) =>
                isFullOption
                  ? handleChange(range.dbKey, e.target.value)
                  : handleInteraction(e)
              }
              onClick={handleInteraction}
              onFocus={handleInteraction}
              placeholder={initialQuotes[range.dbKey]}
              disabled={!isFullOption && isLockedPreview}
              $isLocked={!isFullOption && isLockedPreview}
              rows={isMobile ? "2" : "3"}
            />
            <QuotePreview $isMobile={isMobile}>
              <FaQuoteLeft size={isMobile ? 10 : 12} />
              <span>
                {localQuotes[range.dbKey] || initialQuotes[range.dbKey]}
              </span>
              <FaQuoteRight size={isMobile ? 10 : 12} />
            </QuotePreview>
          </QuoteInputGroup>
        ))}
      </QuoteContent>

      {isFullOption ? (
        <QuoteActions $isMobile={isMobile}>
          <SaveButton
            onClick={handleSaveQuotes}
            disabled={
              !hasContent || status.includes(t("settings.status.saving"))
            }
            $isMobile={isMobile}
          >
            <FaSave />
            {isMobile
              ? t("settings.actions.save")
              : status.includes(t("settings.status.saving"))
              ? t("settings.status.saving")
              : t("settings.quotes.save")}
          </SaveButton>
          {status && (
            <StatusMessage
              $error={status.includes(t("settings.status.error"))}
              $isMobile={isMobile}
            >
              {isMobile ? status.substring(0, 20) + "..." : status}
            </StatusMessage>
          )}
        </QuoteActions>
      ) : (
        <MobileUpgradePrompt>
          <FaLock size={14} />
          <span>{t("settings.upgrade.unlockWithPro")}</span>
          <MobileUpgradeLink href="/upgrade">
            {t("settings.upgrade.upgrade")}
          </MobileUpgradeLink>
        </MobileUpgradePrompt>
      )}
    </QuoteSection>
  );
}

// ----------------------------------------------------
//       CURRENCY SETTINGS COMPONENT
// ----------------------------------------------------
function CurrencySettings() {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const [selectedCurrency, setSelectedCurrency] = useState(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const settingsDocRef = userId
    ? doc(db, "users", userId, "settings", "currency")
    : null;

  useEffect(() => {
    if (!settingsDocRef || !userId) {
      setLoading(false);
      return;
    }

    const fetchCurrency = async () => {
      try {
        const docSnap = await getDoc(settingsDocRef);
        if (docSnap.exists() && docSnap.data().currency) {
          setSelectedCurrency(docSnap.data().currency);
        }
      } catch (error) {
        console.error("Error fetching currency settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrency();
  }, [userId]);

  const handleCurrencyChange = (e) => {
    const selectedCode = e.target.value;
    const currency = CURRENCY_OPTIONS.find((c) => c.code === selectedCode);
    if (currency) setSelectedCurrency(currency);
  };

  const saveCurrency = async () => {
    if (!userId || !settingsDocRef) return;

    setStatus(t("settings.status.saving"));
    try {
      await setDoc(
        settingsDocRef,
        {
          currency: selectedCurrency,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setStatus(t("settings.status.saved"));
      setTimeout(() => setStatus(""), 3000);
    } catch (error) {
      console.error("Error saving currency:", error);
      setStatus(t("settings.status.error"));
      setTimeout(() => setStatus(""), 5000);
    }
  };

  if (loading) {
    return (
      <CurrencySection>
        <LoadingState>{t("settings.status.loading")}</LoadingState>
      </CurrencySection>
    );
  }

  return (
    <CurrencySection>
      <CurrencyHeader>
        <CurrencyIcon>
          <FaMoneyBillWave />
        </CurrencyIcon>
        <CurrencyTitle>{t("settings.currency.title")}</CurrencyTitle>
      </CurrencyHeader>

      <CurrencyDescription>
        {t("settings.currency.description")}
      </CurrencyDescription>

      <CurrencySelector>
        <CurrencySelect
          value={selectedCurrency.code}
          onChange={handleCurrencyChange}
        >
          {CURRENCY_OPTIONS.map((currency) => (
            <option key={currency.code} value={currency.code}>
              {isMobile
                ? `${currency.symbol} ${currency.code}`
                : `${currency.symbol} ${currency.name}`}
            </option>
          ))}
        </CurrencySelect>
      </CurrencySelector>

      <CurrencyPreview $isMobile={isMobile}>
        <PreviewLabel>{t("settings.currency.preview")}</PreviewLabel>
        <PreviewAmount $isMobile={isMobile}>
          {selectedCurrency.symbol}1,234.56
        </PreviewAmount>
        {!isMobile && (
          <PreviewDescription>
            {t("settings.currency.example")}
          </PreviewDescription>
        )}
      </CurrencyPreview>

      <CurrencyActions $isMobile={isMobile}>
        <SaveButton onClick={saveCurrency} $isMobile={isMobile}>
          <FaSave />
          {isMobile
            ? t("settings.actions.save")
            : status.includes(t("settings.status.saving"))
            ? t("settings.status.saving")
            : t("settings.currency.save")}
        </SaveButton>
        {status && (
          <StatusMessage
            $error={status.includes(t("settings.status.error"))}
            $isMobile={isMobile}
          >
            {isMobile ? status.substring(0, 10) : status}
          </StatusMessage>
        )}
      </CurrencyActions>
    </CurrencySection>
  );
}

// ----------------------------------------------------
//       SUBSCRIPTION COMPONENT
// ----------------------------------------------------
function SubscriptionSettings() {
  const { t } = useTranslation();
  const { subscriptionTier } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      const functions = getFunctions();
      const createPortal = httpsCallable(functions, "createCustomerPortal");
      const res = await createPortal();
      window.location.href = res.data.url;
    } catch (error) {
      console.error("Error opening portal:", error);
      alert(t("settings.subscription.portalError"));
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <SubscriptionSection>
      <SubscriptionHeader>
        <SubscriptionIcon>
          <FaCreditCard />
        </SubscriptionIcon>
        <SubscriptionTitle>
          {t("settings.subscription.title")}
        </SubscriptionTitle>
      </SubscriptionHeader>

      <SubscriptionContent>
        <PlanBadge $isPro={subscriptionTier === PACKAGES.PRO}>
          <FaCheck />
          {subscriptionTier === PACKAGES.PRO
            ? t("settings.subscription.proPlan")
            : t("settings.subscription.freePlan")}
        </PlanBadge>

        <PlanFeatures $isMobile={isMobile}>
          {subscriptionTier === PACKAGES.PRO ? (
            <>
              <FeatureItem>
                <FaCheck /> {t("settings.subscription.features.analytics")}
              </FeatureItem>
              <FeatureItem>
                <FaCheck /> {t("settings.subscription.features.customQuotes")}
              </FeatureItem>
              <FeatureItem>
                <FaCheck />{" "}
                {t("settings.subscription.features.prioritySupport")}
              </FeatureItem>
              <FeatureItem>
                <FaCheck /> {t("settings.subscription.features.unlimited")}
              </FeatureItem>
            </>
          ) : (
            <>
              <FeatureItem>
                <FaCheck /> {t("settings.subscription.features.basic")}
              </FeatureItem>
              <FeatureItem>
                <FaCheck /> {t("settings.subscription.features.limited")}
              </FeatureItem>
              <FeatureItem>
                <FaCheck /> {t("settings.subscription.features.community")}
              </FeatureItem>
              <FeatureItem>
                <FaLock /> {t("settings.subscription.features.custom")}
              </FeatureItem>
            </>
          )}
        </PlanFeatures>
      </SubscriptionContent>

      <SubscriptionActions $isMobile={isMobile}>
        <PortalButton
          onClick={openPortal}
          disabled={portalLoading}
          $isMobile={isMobile}
        >
          {portalLoading ? <FaSync className="spin" /> : <FaUserCog />}
          {isMobile
            ? t("settings.subscription.manage")
            : portalLoading
            ? t("settings.status.loading")
            : t("settings.subscription.manageSubscription")}
        </PortalButton>
        {subscriptionTier !== PACKAGES.PRO && (
          <MobileUpgradeButton href="/upgrade" $isMobile={isMobile}>
            {isMobile
              ? t("settings.upgrade.upgrade")
              : t("settings.subscription.upgradeToPro")}
          </MobileUpgradeButton>
        )}
      </SubscriptionActions>
    </SubscriptionSection>
  );
}

// ----------------------------------------------------
//       MAIN COMPONENT: Settings
// ----------------------------------------------------
export default function Settings({ theme, switchTheme }) {
  const { t } = useTranslation();
  const { loading } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) {
    return (
      <SettingsContainer>
        <LoadingContainer>
          <FaSync className="spin" size={24} />
          <p>{t("settings.status.loadingSettings")}</p>
        </LoadingContainer>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <Navbar theme={theme} switchTheme={switchTheme} />

      <SettingsHeader $isMobile={isMobile}>
        <HeaderContent>
          <MainTitle $isMobile={isMobile}>{t("settings.title")}</MainTitle>
          <Subtitle $isMobile={isMobile}>
            {isMobile
              ? t("settings.subtitle.short")
              : t("settings.subtitle.full")}
          </Subtitle>
        </HeaderContent>
        {!isMobile && (
          <HeaderDecoration>
            <FaCog className="spin-slow" size={48} />
          </HeaderDecoration>
        )}
      </SettingsHeader>

      <ContentWrapper>
        <SettingsGrid $isMobile={isMobile}>
          <LeftColumn>
            <SubscriptionSettings />
            <CurrencySettings />
          </LeftColumn>

          <RightColumn>
            <QuoteGrid $isMobile={isMobile}>
              <ProgressQuoteSettings
                type="tasks"
                initialQuotes={TASK_INITIAL_QUOTES}
              />
              <ProgressQuoteSettings
                type="habits"
                initialQuotes={HABIT_INITIAL_QUOTES}
              />
            </QuoteGrid>

            {!isMobile && (
              <AdditionalSettings>
                <SettingCard>
                  <SettingIcon>
                    <FaBell />
                  </SettingIcon>
                  <SettingTitle>
                    {t("settings.additional.notifications")}
                  </SettingTitle>
                  <SettingDescription>
                    {t("settings.additional.notificationsDesc")}
                  </SettingDescription>
                </SettingCard>
                <SettingCard>
                  <SettingIcon>
                    <FaChartLine />
                  </SettingIcon>
                  <SettingTitle>
                    {t("settings.additional.analytics")}
                  </SettingTitle>
                  <SettingDescription>
                    {t("settings.additional.analyticsDesc")}
                  </SettingDescription>
                </SettingCard>
                <SettingCard>
                  <SettingIcon>
                    <FaShieldAlt />
                  </SettingIcon>
                  <SettingTitle>
                    {t("settings.additional.privacy")}
                  </SettingTitle>
                  <SettingDescription>
                    {t("settings.additional.privacyDesc")}
                  </SettingDescription>
                </SettingCard>
                <SettingCard>
                  <SettingIcon>
                    <FaDatabase />
                  </SettingIcon>
                  <SettingTitle>{t("settings.additional.data")}</SettingTitle>
                  <SettingDescription>
                    {t("settings.additional.dataDesc")}
                  </SettingDescription>
                </SettingCard>
              </AdditionalSettings>
            )}
          </RightColumn>
        </SettingsGrid>
      </ContentWrapper>
    </SettingsContainer>
  );
}
// ----------------------------------------------------
//       STYLED COMPONENTS (Mobile Optimized)
// ----------------------------------------------------
const SettingsContainer = styled.div`
  padding: ${(props) => (props.$isMobile ? "0 0.75rem" : "0 2rem")};
  min-height: 100vh;
  background: var(--primaryBackgroundColor);
  color: var(--fontPrimaryColor);
  transition: 0.5s;
  overflow: hidden;
  padding-bottom: ${(props) => (props.$isMobile ? "80px" : "2rem")};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 0 0.5rem;
    padding-bottom: 80px;
  }
  @media (max-width: 750px) {
    padding-top: 110px;
  }

  @media (max-width: 320px) {
    padding: 0 0.25rem;
    padding-bottom: 80px;
  }

  // Prevent horizontal scroll
  overflow-x: hidden;
  max-width: 100vw;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  gap: 1rem;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SettingsHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: ${(props) => (props.$isMobile ? "flex-start" : "center")};
  padding: ${(props) => (props.$isMobile ? "1.5rem 0 1rem" : "3rem 0 2rem")};
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--thirdBackgroundColor);
  flex-direction: ${(props) => (props.$isMobile ? "column" : "row")};
  gap: ${(props) => (props.$isMobile ? "1rem" : "0")};

  @media (max-width: 480px) {
    padding: 1rem 0;
  }
`;

const HeaderContent = styled.div``;

const MainTitle = styled.h1`
  font-size: ${(props) => (props.$isMobile ? "2rem" : "2.5rem")};
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-background-clip: text;
  background-clip: text;

  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: ${(props) => (props.$isMobile ? "0.95rem" : "1.1rem")};
  margin-top: 0.5rem;

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const HeaderDecoration = styled.div`
  color: var(--primaryColor);
  opacity: 0.7;

  .spin-slow {
    animation: spin-slow 20s linear infinite;
  }

  @keyframes spin-slow {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$isMobile ? "1fr" : "1fr 2fr")};
  gap: ${(props) => (props.$isMobile ? "1rem" : "2rem")};
  margin-bottom: ${(props) => (props.$isMobile ? "1rem" : "3rem")};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$isMobile ? "1rem" : "2rem")};
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$isMobile ? "1rem" : "2rem")};
`;

// Subscription Section Styles
const SubscriptionSection = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 12px;
  padding: ${(props) => (props.$isMobile ? "1.25rem" : "2rem")};
  position: relative;
  overflow: hidden;
  border: 1px solid var(--shadowColor);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 1rem;
    width: 100%;
    margin: 0;
  }
`;

const SubscriptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$isMobile ? "0.75rem" : "1rem")};
  margin-bottom: 1rem;
`;

const SubscriptionIcon = styled.div`
  color: var(--primaryColor);
  font-size: ${(props) => (props.$isMobile ? "1.25rem" : "1.5rem")};
`;

const SubscriptionTitle = styled.h2`
  font-size: ${(props) => (props.$isMobile ? "1.25rem" : "1.5rem")};
  font-weight: 600;
  margin: 0;
  color: var(--fontPrimaryColor);
`;

const SubscriptionContent = styled.div`
  margin-bottom: 1.5rem;
`;

const PlanBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: ${(props) => (props.$isMobile ? "0.875rem" : "1rem")};
  background: ${(props) =>
    props.$isPro
      ? "linear-gradient(135deg, #8b5cf6, #6366f1)"
      : "var(--thirdBackgroundColor)"};
  color: ${(props) => (props.$isPro ? "white" : "var(--fontPrimaryColor)")};
  margin-bottom: 1rem;
`;

const PlanFeatures = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$isMobile ? "1fr 1fr" : "1fr 1fr"};
  gap: ${(props) => (props.$isMobile ? "0.5rem" : "0.75rem")};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${(props) => (props.$isMobile ? "0.8rem" : "0.9rem")};
  color: var(--fontSecondaryColor);

  svg {
    color: var(--primaryColor);
    font-size: ${(props) => (props.$isMobile ? "0.75rem" : "0.875rem")};
  }
`;

const SubscriptionActions = styled.div`
  display: flex;
  gap: ${(props) => (props.$isMobile ? "0.75rem" : "1rem")};
  flex-direction: ${(props) => (props.$isMobile ? "column" : "row")};
`;

const PortalButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => (props.$isMobile ? "0.5rem" : "0.75rem")};
  padding: ${(props) => (props.$isMobile ? "0.75rem" : "0.875rem 1.5rem")};
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  border: 1px solid var(--shadowColor);
  border-radius: 8px;
  font-weight: 500;
  font-size: ${(props) => (props.$isMobile ? "0.875rem" : "1rem")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: var(--lowPopupBg);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }
`;

const MobileUpgradeButton = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: ${(props) => (props.$isMobile ? "0.875rem" : "1rem")};
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  }
`;

// Currency Section Styles
const CurrencySection = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 12px;
  padding: ${(props) => (props.$isMobile ? "1.25rem" : "2rem")};
  border: 1px solid var(--shadowColor);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 1rem;
    width: 100%;
  }
`;

const CurrencyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$isMobile ? "0.75rem" : "1rem")};
  margin-bottom: 1rem;
`;

const CurrencyIcon = styled.div`
  color: var(--primaryColor);
  font-size: ${(props) => (props.$isMobile ? "1.25rem" : "1.5rem")};
`;

const CurrencyTitle = styled.h2`
  font-size: ${(props) => (props.$isMobile ? "1.25rem" : "1.5rem")};
  font-weight: 600;
  margin: 0;
  color: var(--fontPrimaryColor);
`;

const CurrencyDescription = styled.p`
  color: var(--fontSecondaryColor);
  margin-bottom: 1.5rem;
  font-size: ${(props) => (props.$isMobile ? "0.9rem" : "1rem")};
  line-height: 1.5;
`;

const CurrencySelector = styled.div`
  margin-bottom: 1.5rem;
`;

const CurrencySelect = styled.select`
  width: 100%;
  padding: ${(props) => (props.$isMobile ? "0.75rem" : "0.875rem 1rem")};
  border-radius: 8px;
  border: 1px solid var(--shadowColor);
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  font-size: ${(props) => (props.$isMobile ? "0.9rem" : "1rem")};
  cursor: pointer;
  transition: all 0.3s ease;
  box-sizing: border-box;
  max-width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primaryColor);
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1);
  }
`;

const CurrencyPreview = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
  padding: ${(props) => (props.$isMobile ? "1rem" : "1.5rem")};
  margin-bottom: 1.5rem;
  border: 1px solid var(--shadowColor);
`;

const PreviewLabel = styled.div`
  font-size: ${(props) => (props.$isMobile ? "0.8rem" : "0.875rem")};
  color: var(--fontSecondaryColor);
  margin-bottom: 0.5rem;
`;

const PreviewAmount = styled.div`
  font-size: ${(props) => (props.$isMobile ? "1.5rem" : "2rem")};
  font-weight: 700;
  color: var(--fontPrimaryColor);
  margin-bottom: 0.5rem;
`;

const PreviewDescription = styled.div`
  font-size: 0.875rem;
  color: var(--fontSecondaryColor);
`;

const CurrencyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-direction: ${(props) => (props.$isMobile ? "column" : "row")};
`;

// Quote Section Styles
const QuoteGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$isMobile ? "1fr" : "1fr 1fr")};
  gap: ${(props) => (props.$isMobile ? "1rem" : "2rem")};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QuoteSection = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 12px;
  padding: ${(props) => (props.$isMobile ? "1.25rem" : "2rem")};
  position: relative;
  border: 1px solid var(--shadowColor);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 480px) {
    padding: 1rem;
    width: 100%;
  }
`;

const QuoteHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$isMobile ? "0.75rem" : "1rem")};
  margin-bottom: 1rem;
  position: relative;
`;

const QuoteIcon = styled.div`
  color: var(--primaryColor);
  font-size: ${(props) => (props.$isMobile ? "1.25rem" : "1.5rem")};
`;

const QuoteTitle = styled.h2`
  font-size: ${(props) => (props.$isMobile ? "1.25rem" : "1.5rem")};
  font-weight: 600;
  margin: 0;
  color: var(--fontPrimaryColor);
  flex: 1;
`;

const QuoteBadge = styled.div`
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  font-size: ${(props) => (props.$isMobile ? "0.7rem" : "0.75rem")};
  font-weight: 600;
  background: ${(props) =>
    props.$isPro
      ? "linear-gradient(135deg, #8b5cf6, #6366f1)"
      : "var(--thirdBackgroundColor)"};
  color: ${(props) => (props.$isPro ? "white" : "var(--fontSecondaryColor)")};
`;

const QuoteDescription = styled.p`
  color: var(--fontSecondaryColor);
  margin-bottom: 1.5rem;
  font-size: ${(props) => (props.$isMobile ? "0.9rem" : "1rem")};
  line-height: 1.5;
`;

const QuoteTabs = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  width: 100%;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 3px;
  }

  &::-webkit-scrollbar-track {
    background: var(--thirdBackgroundColor);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--primaryColor);
    border-radius: 2px;
  }

  // On very small screens, make it scrollable but contained
  @media (max-width: 480px) {
    margin-left: -0.25rem;
    margin-right: -0.25rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
`;

const QuoteTab = styled.button`
  padding: ${(props) => (props.$isMobile ? "0.4rem 0.6rem" : "0.5rem 1rem")};
  border: 1px solid
    ${(props) => (props.$active ? "var(--primaryColor)" : "var(--shadowColor)")};
  background: ${(props) =>
    props.$active ? "var(--primaryColor)" : "transparent"};
  color: ${(props) => (props.$active ? "white" : "var(--fontSecondaryColor)")};
  border-radius: 6px;
  font-size: ${(props) => (props.$isMobile ? "0.75rem" : "0.875rem")};
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    border-color: var(--primaryColor);
    color: var(--fontPrimaryColor);
  }
`;

const QuoteContent = styled.div`
  position: relative;
  min-height: ${(props) => (props.$isMobile ? "150px" : "200px")};
`;

const QuoteInputGroup = styled.div`
  display: ${(props) => (props.$active ? "block" : "none")};
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const QuoteRangeLabel = styled.div`
  margin-bottom: 1rem;

  span {
    font-weight: 600;
    color: var(--fontPrimaryColor);
    display: block;
    margin-bottom: 0.25rem;
    font-size: ${(props) => (props.$isMobile ? "0.95rem" : "1rem")};
  }

  small {
    font-size: ${(props) => (props.$isMobile ? "0.75rem" : "0.875rem")};
    color: var(--fontSecondaryColor);
  }
`;

const QuoteTextArea = styled.textarea`
  width: 100%;
  padding: ${(props) => (props.$isMobile ? "0.75rem" : "1rem")};
  border-radius: 8px;
  border: 1px solid var(--shadowColor);
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  font-size: ${(props) => (props.$isMobile ? "0.9rem" : "1rem")};
  line-height: 1.4;
  resize: vertical;
  min-height: ${(props) => (props.$isMobile ? "80px" : "100px")};
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  max-width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primaryColor);
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    filter: ${(props) => (props.$isLocked ? "blur(2px)" : "none")};
  }
`;

const QuotePreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$isMobile ? "0.5rem" : "0.75rem")};
  padding: ${(props) => (props.$isMobile ? "0.75rem" : "1rem")};
  background: var(--thirdBackgroundColor);
  border-radius: 8px;
  border: 1px solid var(--shadowColor);
  font-style: italic;
  color: var(--fontSecondaryColor);
  font-size: ${(props) => (props.$isMobile ? "0.85rem" : "0.95rem")};

  svg:first-child {
    color: var(--primaryColor);
    opacity: 0.5;
    flex-shrink: 0;
  }

  svg:last-child {
    color: var(--primaryColor);
    opacity: 0.5;
    margin-left: auto;
    flex-shrink: 0;
  }

  span {
    flex: 1;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const QuoteActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-direction: ${(props) => (props.$isMobile ? "column" : "row")};
`;

// Common Components
const SaveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => (props.$isMobile ? "0.5rem" : "0.75rem")};
  padding: ${(props) => (props.$isMobile ? "0.75rem" : "0.875rem 1.5rem")};
  background: var(--primaryColor);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: ${(props) => (props.$isMobile ? "0.875rem" : "1rem")};
  cursor: pointer;
  transition: all 0.3s ease;
  width: ${(props) => (props.$isMobile ? "100%" : "auto")};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  color: ${(props) => (props.$error ? "#ef4444" : "#10b981")};
  font-size: ${(props) => (props.$isMobile ? "0.75rem" : "0.875rem")};
  font-weight: 500;
  text-align: ${(props) => (props.$isMobile ? "center" : "left")};
  width: ${(props) => (props.$isMobile ? "100%" : "auto")};
`;

const MobileUpgradeOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(var(--secondaryBackgroundColor), 0.95);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
  z-index: 10;
  border-radius: 12px;

  h3 {
    margin: 0.75rem 0 0.25rem;
    color: var(--fontPrimaryColor);
    font-size: 1.1rem;
  }

  p {
    color: var(--fontSecondaryColor);
    margin-bottom: 1rem;
    font-size: 0.9rem;
    max-width: 250px;
  }
`;

const MobileUpgradeLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  }
`;

const MobileUpgradePrompt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
  margin-top: 1.5rem;
  border: 1px dashed var(--shadowColor);
  flex-wrap: wrap;

  svg {
    color: var(--primaryColor);
    font-size: 0.875rem;
  }

  span {
    color: var(--fontSecondaryColor);
    font-weight: 500;
    font-size: 0.875rem;
  }
`;

const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--fontSecondaryColor);
  font-style: italic;
  font-size: 0.9rem;
`;

// Additional Settings Cards (hidden on mobile)
const AdditionalSettings = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SettingCard = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 10px;
  padding: 1.25rem;
  border: 1px solid var(--shadowColor);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    border-color: var(--primaryColor);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const SettingIcon = styled.div`
  color: var(--primaryColor);
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
`;

const SettingTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
  color: var(--fontPrimaryColor);
`;

const SettingDescription = styled.p`
  font-size: 0.8rem;
  color: var(--fontSecondaryColor);
  margin: 0;
`;
